import { request } from './core'
import type {
  Company,
  CompanySummary,
  ModuleQuestionnaire,
  AssessmentState,
} from './types'

export const companiesApi = {
  listCompanies: () => request<CompanySummary[]>('/api/companies'),

  createCompany: (params: {
    display_name: string
    company_url?: string | undefined
    research?: boolean | undefined
  }) =>
    request<{ slug: string; company: Company }>('/api/companies', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  getCompany: (slug: string) => request<Company>(`/api/companies/${slug}`),

  deleteCompany: (slug: string, revision?: number) =>
    request<{ deleted: number }>(
      `/api/companies/${slug}`,
      { method: 'DELETE' },
      { ifMatch: revision },
    ),

  /** Update the company's website URL (drives web research + the derived logo). */
  putCompanyIdentity: (slug: string, company_url: string) =>
    request<{ ok: boolean; company_url: string | null; logo_url: string | null; revision: number }>(
      `/api/companies/${slug}/identity`,
      { method: 'PUT', body: JSON.stringify({ company_url }) },
    ),

  /** Save one company-profile section (changed keys only, optimistic retry
   *  server-side — same contract as putSectionFields). */
  putCompanyFields: (slug: string, sectionKey: string, values: Record<string, unknown>) =>
    request<{
      ok: boolean
      section: string
      saved_keys: string[]
      unknown_keys: string[]
      revision: number
    }>(`/api/companies/${slug}/profile/fields/${sectionKey}`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    }),

  /** Start the AI Assessment module for an existing company (409 if present).
   *  The funding program is quarterly, so the assessment is assigned its
   *  period up front; omitted → the current quarter, server-side. */
  createCompanyAssessment: (slug: string, period?: { year: number; quarter: number }) =>
    request<{ slug: string; state: AssessmentState }>(`/api/companies/${slug}/assessment`, {
      method: 'POST',
      body: JSON.stringify(
        period ? { period_year: period.year, period_quarter: period.quarter } : {},
      ),
    }),

  /** Create (idempotent) a WAFR/FTR module instance. */
  createModule: (slug: string, module: string) =>
    request<ModuleQuestionnaire>(`/api/companies/${slug}/modules/${module}`, {
      method: 'POST',
    }),

  getModule: (slug: string, module: string) =>
    request<ModuleQuestionnaire>(`/api/companies/${slug}/modules/${module}`),

  /** Save one module questionnaire section. Shared baseline sections land on
   *  the company store server-side; the caller doesn't need to care. */
  putModuleFields: (
    slug: string,
    module: string,
    sectionKey: string,
    values: Record<string, unknown>,
  ) =>
    request<{
      ok: boolean
      section: string
      saved_keys: string[]
      unknown_keys: string[]
      revision: number
      status: string
    }>(`/api/companies/${slug}/modules/${module}/fields/${sectionKey}`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    }),
}
